import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import UpcomingAppointments from './components/UpcomingAppointments';
import RecentTestResults from './components/RecentTestResults';
import CurrentMedications from './components/CurrentMedications';
import HealthAlerts from './components/HealthAlerts';
import SecureMessaging from './components/SecureMessaging';
import BillingSummary from './components/BillingSummary';
import HealthTracking from './components/HealthTracking';
import DentalRecordsAccess from './components/MedicalRecordsAccess';
import Icon from '../../components/AppIcon';


const PatientPortal = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1985",
    age: "41 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: 'warning',
      title: 'Annual Physical Due',
      message: 'Your annual physical examination is due. Please schedule an appointment with your primary care provider.',
      actionRequired: 'Schedule appointment within 30 days',
      actionButton: 'Schedule Now',
      dismissible: true
    },
    {
      id: 2,
      severity: 'info',
      title: 'Flu Shot Available',
      message: 'Flu season is here. Protect yourself and your family by getting your annual flu vaccination.',
      actionButton: 'Book Vaccination',
      dismissible: true
    }
  ];

  const upcomingAppointments = [
    {
      id: 1,
      providerName: "Dr. Michael Chen",
      specialty: "General Dentist",
      type: "routine-cleaning",
      typeLabel: "Routine Cleaning",
      date: "2026-01-20",
      time: "10:30",
      location: "Main Dental Center - Suite 201",
      telehealth: false
    },
    {
      id: 2,
      providerName: "Dr. Emily Rodriguez",
      specialty: "Periodontist",
      type: "specialist",
      typeLabel: "Specialist",
      date: "2026-01-28",
      time: "14:00",
      location: "Periodontal Center - 3rd Floor",
      telehealth: false
    },
    {
      id: 3,
      providerName: "Dr. James Wilson",
      specialty: "Endodontist",
      type: "follow-up",
      typeLabel: "Follow-up",
      date: "2026-02-05",
      time: "09:00",
      location: "Virtual Visit",
      telehealth: true
    }
  ];

  const recentTestResults = [
    {
      id: 1,
      testName: "Panoramic X-Ray",
      orderedBy: "Dr. Michael Chen",
      date: "2026-01-10",
      status: "normal",
      statusLabel: "Normal",
      newResult: true
    },
    {
      id: 2,
      testName: "Periodontal Charting",
      orderedBy: "Dr. Emily Rodriguez",
      date: "2026-01-08",
      status: "abnormal",
      statusLabel: "Abnormal - Review Required",
      newResult: true
    },
    {
      id: 3,
      testName: "Pulp Vitality Test",
      orderedBy: "Dr. James Wilson",
      date: "2026-01-05",
      status: "normal",
      statusLabel: "Normal",
      newResult: false
    }
  ];

  const currentMedications = [
    {
      id: 1,
      name: "Amoxicillin",
      dosage: "500mg",
      frequency: "Three times daily",
      prescribedBy: "Dr. Michael Chen",
      daysRemaining: 5,
      refillsRemaining: 0
    },
    {
      id: 2,
      name: "Ibuprofen",
      dosage: "600mg",
      frequency: "As needed for pain",
      prescribedBy: "Dr. James Wilson",
      daysRemaining: 15,
      refillsRemaining: 2
    },
    {
      id: 3,
      name: "Chlorhexidine Rinse",
      dosage: "0.12%",
      frequency: "Twice daily",
      prescribedBy: "Dr. Emily Rodriguez",
      daysRemaining: 20,
      refillsRemaining: 1
    }
  ];

  const secureMessages = [
    {
      id: 1,
      from: "Dr. Michael Chen",
      subject: "Follow-up on Recent Lab Results",
      preview: "Your recent blood work looks good. I\'d like to discuss a few minor adjustments to your medication during your next visit.",
      date: "2026-01-13",
      unread: true,
      hasAttachment: false
    },
    {
      id: 2,
      from: "Billing Department",
      subject: "Payment Confirmation",
      preview: "Thank you for your recent payment of $150.00. Your account balance is now $0.00.",
      date: "2026-01-12",
      unread: false,
      hasAttachment: true
    },
    {
      id: 3,
      from: "Appointment Scheduler",
      subject: "Appointment Reminder",
      preview: "This is a reminder of your upcoming appointment with Dr. Michael Chen on January 20, 2026 at 10:30 AM.",
      date: "2026-01-11",
      unread: false,
      hasAttachment: false
    }
  ];

  const billingStatements = [
    {
      id: 1,
      description: "Office Visit - Primary Care",
      date: "2025-12-15",
      dueDate: "2026-01-15",
      amount: 150.00,
      status: "pending",
      statusLabel: "Pending"
    },
    {
      id: 2,
      description: "Laboratory Services",
      date: "2025-12-10",
      dueDate: "2026-01-10",
      amount: 85.00,
      status: "overdue",
      statusLabel: "Overdue"
    }
  ];

  const healthTrackingData = [
    {
      id: 1,
      metric: "blood-pressure",
      value: "120/80",
      notes: "Measured in the morning",
      date: "2026-01-14T08:30:00"
    },
    {
      id: 2,
      metric: "blood-glucose",
      value: "95",
      notes: "Fasting",
      date: "2026-01-14T07:00:00"
    },
    {
      id: 3,
      metric: "weight",
      value: "165",
      notes: "",
      date: "2026-01-13T09:00:00"
    }
  ];

  const medicalRecords = [
    {
      id: 1,
      title: "Comprehensive Oral Examination",
      provider: "Dr. Michael Chen",
      date: "2025-12-15",
      category: "visit-summaries",
      categoryLabel: "Visit Summary",
      hasAttachment: true
    },
    {
      id: 2,
      title: "Panoramic Radiograph Results",
      provider: "Dental Imaging Center",
      date: "2026-01-10",
      category: "lab-results",
      categoryLabel: "Diagnostic Results",
      hasAttachment: true
    },
    {
      id: 3,
      title: "Periodontal Charting Report",
      provider: "Dr. Emily Rodriguez",
      date: "2025-11-20",
      category: "imaging",
      categoryLabel: "Diagnostic",
      hasAttachment: true
    },
    {
      id: 4,
      title: "Treatment Plan - Crown Preparation",
      provider: "Dr. Michael Chen",
      date: "2025-10-15",
      category: "visit-summaries",
      categoryLabel: "Treatment Plan",
      hasAttachment: true
    },
    {
      id: 5,
      title: "Prescription - Amoxicillin",
      provider: "Dr. Michael Chen",
      date: "2026-01-15",
      category: "prescriptions",
      categoryLabel: "Prescriptions",
      hasAttachment: false
    }
  ];

  const quickActions = [
    {
      id: 'schedule',
      label: 'Schedule Appointment',
      icon: 'Calendar',
      onClick: () => navigate('/appointment-scheduling'),
      shortcut: 'Ctrl+S'
    },
    {
      id: 'message',
      label: 'Send Message',
      icon: 'MessageSquare',
      onClick: () => setActiveTab('messages'),
      shortcut: 'Ctrl+M'
    },
    {
      id: 'refill',
      label: 'Request Refill',
      icon: 'Pill',
      onClick: () => setActiveTab('medications'),
      shortcut: 'Ctrl+R'
    },
    {
      id: 'records',
      label: 'View Records',
      icon: 'FolderOpen',
      onClick: () => setActiveTab('records'),
      shortcut: 'Ctrl+V'
    }
  ];

  const handleScheduleNew = () => {
    navigate('/appointment-scheduling');
  };

  const handleViewAppointmentDetails = (appointment) => {
    console.log('View appointment details:', appointment);
  };

  const handleViewAllResults = () => {
    navigate('/lab-results');
  };

  const handleViewResult = (result) => {
    console.log('View test result:', result);
  };

  const handleViewAllMedications = () => {
    navigate('/medication-management');
  };

  const handleRequestRefill = (medication) => {
    console.log('Request refill for:', medication);
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismiss alert:', alertId);
  };

  const handleViewAlertDetails = (alert) => {
    console.log('View alert details:', alert);
  };

  const handleSendMessage = (message) => {
    console.log('Send message:', message);
  };

  const handleViewMessageThread = (message) => {
    console.log('View message thread:', message);
  };

  const handlePayBill = (bill) => {
    navigate('/billing-management');
  };

  const handleViewStatement = () => {
    navigate('/billing-management');
  };

  const handleAddHealthEntry = (entry) => {
    console.log('Add health tracking entry:', entry);
  };

  const handleViewRecord = (record) => {
    console.log('View medical record:', record);
  };

  const handleDownloadRecord = (record) => {
    console.log('Download record:', record);
  };

  const handleSessionExpired = () => {
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'appointments', label: 'Appointments', icon: 'Calendar' },
    { id: 'results', label: 'Test Results', icon: 'FlaskConical' },
    { id: 'medications', label: 'Medications', icon: 'Pill' },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
    { id: 'billing', label: 'Billing', icon: 'DollarSign' },
    { id: 'tracking', label: 'Health Tracking', icon: 'Activity' },
    { id: 'records', label: 'Medical Records', icon: 'FolderOpen' }
  ];

  return (
    <div className="min-h-screen bg-background pt-16">
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />

      <div className={`transition-all duration-250 ease-out ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
        <PatientContextBar 
          patient={currentPatient}
          alerts={patientAlerts}
          onAlertClick={handleViewAlertDetails}
        />

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground flex items-center gap-3">
                <Icon name="User" size={36} className="text-primary" />
                Patient Portal
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                Access your dental health information, appointments, and communicate with your dental care team
              </p>
            </div>

            <div className="bg-card rounded-xl border border-border p-2 mb-6 md:mb-8 overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span className="hidden md:inline">{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-6">
                <HealthAlerts 
                  alerts={patientAlerts}
                  onDismiss={handleDismissAlert}
                  onViewDetails={handleViewAlertDetails}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <UpcomingAppointments 
                    appointments={upcomingAppointments}
                    onScheduleNew={handleScheduleNew}
                    onViewDetails={handleViewAppointmentDetails}
                  />
                  <RecentTestResults 
                    results={recentTestResults}
                    onViewAll={handleViewAllResults}
                    onViewResult={handleViewResult}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <CurrentMedications 
                    medications={currentMedications}
                    onRequestRefill={handleRequestRefill}
                    onViewAll={handleViewAllMedications}
                  />
                  <SecureMessaging 
                    messages={secureMessages}
                    onSendMessage={handleSendMessage}
                    onViewThread={handleViewMessageThread}
                  />
                </div>

                <BillingSummary 
                  bills={billingStatements}
                  onPayBill={handlePayBill}
                  onViewStatement={handleViewStatement}
                />
              </div>
            )}

            {activeTab === 'appointments' && (
              <UpcomingAppointments 
                appointments={upcomingAppointments}
                onScheduleNew={handleScheduleNew}
                onViewDetails={handleViewAppointmentDetails}
              />
            )}

            {activeTab === 'results' && (
              <RecentTestResults 
                results={recentTestResults}
                onViewAll={handleViewAllResults}
                onViewResult={handleViewResult}
              />
            )}

            {activeTab === 'medications' && (
              <CurrentMedications 
                medications={currentMedications}
                onRequestRefill={handleRequestRefill}
                onViewAll={handleViewAllMedications}
              />
            )}

            {activeTab === 'messages' && (
              <SecureMessaging 
                messages={secureMessages}
                onSendMessage={handleSendMessage}
                onViewThread={handleViewMessageThread}
              />
            )}

            {activeTab === 'billing' && (
              <BillingSummary 
                bills={billingStatements}
                onPayBill={handlePayBill}
                onViewStatement={handleViewStatement}
              />
            )}

            {activeTab === 'tracking' && (
              <HealthTracking 
                trackingData={healthTrackingData}
                onAddEntry={handleAddHealthEntry}
              />
            )}

            {activeTab === 'records' && (
              <DentalRecordsAccess 
                records={medicalRecords}
                onViewRecord={handleViewRecord}
                onDownload={handleDownloadRecord}
              />
            )}
          </div>
        </main>
      </div>
      <QuickActions actions={quickActions} position="bottom-right" />
      <SessionManager 
        sessionTimeout={900000}
        warningTime={120000}
        onSessionExpired={handleSessionExpired}
        onSessionExtended={handleSessionExtended}
      />
    </div>
  );
};

export default PatientPortal;